/**
 * TerminalTopBar — always-visible bar at the top of the trading terminal.
 * Logo, system status dots, layout presets, add-panel menu, kill switch.
 */

import { useState } from "react";
import { LayoutDashboard } from "lucide-react";
import {
  useAgentsOverview,
  useKillSwitch,
} from "@/sdk/hooks/use-agents";
import { useTerminalStore } from "@/stores/terminal-store";
import type { LayoutPreset } from "@/stores/terminal-store";
import { PANEL_TITLES, type PanelId } from "../layout-presets";
import { TradingModeBadge } from "@/features/trading/components/TradingModeBadge";
import { KillSwitchStatus } from "@/features/trading/components/KillSwitchStatus";

const PRESETS: { key: LayoutPreset; label: string }[] = [
  { key: "default", label: "Default" },
  { key: "monitoring", label: "Monitoring" },
  { key: "trading", label: "Trading" },
];

function StatusDot({ ok, label }: { ok: boolean; label: string }) {
  return (
    <div className="flex items-center gap-1.5 text-xs">
      <span
        className={`h-2 w-2 rounded-full ${ok ? "bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.6)]" : "bg-red-500 shadow-[0_0_6px_rgba(239,68,68,0.6)]"}`}
      />
      <span className="text-zinc-400">{label}</span>
    </div>
  );
}

export function TerminalTopBar({
  onResetLayout,
  missingPanels = [],
  onAddPanel,
}: {
  onResetLayout: (preset: LayoutPreset) => void;
  /** Panels not currently in the layout — offered for re-adding. */
  missingPanels?: PanelId[];
  /** Adds a closed panel back into the layout. */
  onAddPanel?: (panelId: PanelId) => void;
}) {
  const { data: overview } = useAgentsOverview(10_000);
  const killSwitch = useKillSwitch();
  const { activePreset, setActivePreset } = useTerminalStore();
  const [killConfirm, setKillConfirm] = useState(false);
  const [addMenuOpen, setAddMenuOpen] = useState(false);

  const agentCount = overview?.agents
    ? Object.keys(overview.agents).length
    : 0;
  const runningCount = overview?.agents
    ? Object.values(overview.agents).filter((a) => a.state === "running" || a.running === true).length
    : 0;

  const handleKill = () => {
    if (!killConfirm) {
      setKillConfirm(true);
      setTimeout(() => setKillConfirm(false), 3000);
      return;
    }
    killSwitch.mutate();
    setKillConfirm(false);
  };

  const handlePreset = (preset: LayoutPreset) => {
    setActivePreset(preset);
    onResetLayout(preset);
  };

  return (
    <div className="flex h-10 items-center justify-between border-b border-zinc-800 bg-zinc-950 px-2 md:px-3 gap-2">
      {/* Left: Logo + back-to-dashboard + status */}
      <div className="flex items-center gap-2 md:gap-4 min-w-0">
        <span className="text-xs md:text-sm font-bold tracking-tight text-white shrink-0">
          NN<span className="hidden sm:inline">euralNet</span><span className="text-indigo-400">Ninja</span>
        </span>
        {/* The terminal is a full-screen route outside the AppShell — without
            this link the only way back to the main site is typing the URL.
            Plain <a> (same as /remote): keeps the bar Router-independent. */}
        <a
          href="/"
          aria-label="Back to dashboard"
          title="Back to dashboard"
          className="flex items-center gap-1 rounded px-1.5 py-1 text-[0.65rem] md:text-xs font-medium text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-zinc-200 shrink-0"
        >
          <LayoutDashboard className="h-3.5 w-3.5" />
          <span className="hidden md:inline">Dashboard</span>
        </a>
        <div className="hidden sm:flex items-center gap-3">
          <StatusDot
            ok={runningCount > 0}
            label={`${runningCount}/${agentCount} agents`}
          />
          <StatusDot
            ok={overview?.oracle?.state === "running" || Object.values(overview?.oracle?.services ?? {}).some(Boolean)}
            label="Oracle"
          />
          <StatusDot
            ok={(overview?.event_bus?.n_subscribers ?? overview?.event_bus?.subscribers ?? 0) > 0}
            label="Bus"
          />
        </div>
        {/* Mobile: compact status dots only */}
        <div className="flex sm:hidden items-center gap-1.5">
          <span className={`h-2 w-2 rounded-full ${runningCount > 0 ? "bg-emerald-500" : "bg-red-500"}`} title={`${runningCount}/${agentCount} agents`} />
          <span className={`h-2 w-2 rounded-full ${overview?.oracle?.state === "running" || Object.values(overview?.oracle?.services ?? {}).some(Boolean) ? "bg-emerald-500" : "bg-red-500"}`} title="Oracle" />
        </div>
      </div>

      {/* Center: Layout presets + add-panel menu */}
      <div className="flex items-center gap-0.5 md:gap-1">
        {PRESETS.map((p) => (
          <button
            type="button"
            key={p.key}
            onClick={() => handlePreset(p.key)}
            className={`rounded px-1.5 md:px-2.5 py-1 text-[0.65rem] md:text-xs font-medium transition-colors ${
              activePreset === p.key
                ? "bg-indigo-600 text-white"
                : "text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200"
            }`}
          >
            {p.label}
          </button>
        ))}
        <div className="relative">
          <button
            type="button"
            onClick={() => setAddMenuOpen((open) => !open)}
            disabled={missingPanels.length === 0}
            aria-haspopup="menu"
            aria-expanded={addMenuOpen}
            title={
              missingPanels.length === 0
                ? "All panels are open"
                : "Re-add a closed panel"
            }
            className="rounded px-1.5 md:px-2.5 py-1 text-[0.65rem] md:text-xs font-medium text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-zinc-200 disabled:cursor-not-allowed disabled:text-zinc-700 disabled:hover:bg-transparent"
          >
            + Panel
          </button>
          {addMenuOpen && missingPanels.length > 0 ? (
            <div
              role="menu"
              className="absolute left-0 top-full z-50 mt-1 min-w-36 rounded border border-zinc-800 bg-zinc-900 py-1 shadow-lg"
            >
              {missingPanels.map((id) => (
                <button
                  type="button"
                  role="menuitem"
                  key={id}
                  onClick={() => {
                    onAddPanel?.(id);
                    setAddMenuOpen(false);
                  }}
                  className="block w-full px-3 py-1.5 text-left text-xs text-zinc-300 hover:bg-zinc-800 hover:text-white"
                >
                  {PANEL_TITLES[id]}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </div>

      {/* Right: Trading mode + halt state + Kill switch */}
      <div className="flex items-center gap-2 md:gap-3 shrink-0">
        <TradingModeBadge />
        {/* Never hide the halt readout on mobile: an engaged kill switch must
            be visible at every viewport, same rule as the mode badge. */}
        <KillSwitchStatus />
        {killSwitch.isError ? (
          <span role="status" className="text-[0.65rem] font-semibold text-red-400">
            Kill failed
          </span>
        ) : killSwitch.isSuccess ? (
          <span role="status" className="text-[0.65rem] font-semibold text-emerald-400">
            Halt active
          </span>
        ) : null}
        <button
          type="button"
          onClick={handleKill}
          disabled={killSwitch.isPending}
          className={`rounded px-2 md:px-3 py-1 text-[0.65rem] md:text-xs font-bold uppercase tracking-wide transition-all ${
            killConfirm
              ? "animate-pulse bg-red-600 text-white"
              : "border border-red-900/50 text-red-400 hover:bg-red-950 hover:text-red-300"
          }`}
        >
          {killSwitch.isPending
            ? "Halting..."
            : killConfirm
              ? "Confirm"
              : "Emergency Stop"}
        </button>
      </div>
    </div>
  );
}
